import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '../tokens/api-tokens';
import { MemberListItemDto, PagedList } from '../models/member-list.model';
import {
  MemberDetailDto,
  MemberDetailProductDto,
  normalizeMemberDetailResponse,
  normalizeMemberProductsPortalResponse,
  UpdateMemberBasicInfoDto
} from '../models/member-detail.model';
import {
  MemberInvoicePortalDto,
  normalizeMemberInvoicesResponse
} from '../models/member-invoice-portal.model';
import {
  AddMemberProductRequestDto,
  BulkCancelMemberProductsRequestDto,
  CancelMemberProductRequestDto,
  SubClientAvailableProductDto
} from '../models/member-product-endpoints.model';
import {
  MemberDependentRequestDto,
  RemoveMemberDependentRequestDto
} from '../models/member-dependent-endpoints.model';

export interface GetMemberProductsForPortalParams {
  /**
   * When true, only active/current rows. When false or omitted, API default includes history.
   */
  activeOnly?: boolean;
}

export interface GetMembersParams {
  /** When omitted or empty, all subgroups (ccode query param not sent). */
  ccode?: string;
  page: number;
  pageSize: number;
  search?: string;
  /** When omitted, all statuses (query param not sent). Legacy: also skipped when "All". */
  status?: string;
  /** When set, only members with an active enrollment on this client product. */
  clientProductId?: number;
  orderByField?: string;
  orderBy?: 'asc' | 'desc';
}

/** Layout A: one row per member. Layout B: one row per member product. */
export type MemberExportLayout = 'OneRowPerMember' | 'OneRowPerProduct';

export type ExportMembersParams = Omit<GetMembersParams, 'page' | 'pageSize'> & {
  layout: MemberExportLayout;
};

@Injectable({
  providedIn: 'root'
})
export class MemberService {
  private http = inject(HttpClient);
  private apiUrl = inject(API_BASE_URL);

  /** POST client/{mcode}/members/export — 202 queued (link emailed); 400 when nothing matches. */
  exportMembers(mcode: string, params: ExportMembersParams): Observable<{ message: string }> {
    let httpParams = new HttpParams().set('layout', params.layout);

    const ccode = params.ccode?.trim();
    if (ccode) {
      httpParams = httpParams.set('ccode', ccode);
    }

    const search = params.search?.trim();
    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (params.status && params.status !== 'All') {
      httpParams = httpParams.set('status', params.status);
    }

    if (params.clientProductId != null && params.clientProductId > 0) {
      httpParams = httpParams.set('clientProductId', String(params.clientProductId));
    }

    if (params.orderByField) {
      httpParams = httpParams.set('orderByField', params.orderByField);
    }
    if (params.orderBy) {
      httpParams = httpParams.set('orderBy', params.orderBy);
    }

    return this.http.post<{ message: string }>(`${this.apiUrl}/client/${mcode}/members/export`, null, {
      params: httpParams
    });
  }

  /**
   * GET client/{mcode}/members — unwrapped PagedList body.
   * Server contract: list items must not include SSN (strip from API DTO/projection for this route only).
   */
  getMembers(mcode: string, params: GetMembersParams): Observable<PagedList<MemberListItemDto>> {
    let httpParams = new HttpParams()
      .set('page', String(params.page))
      .set('pageSize', String(params.pageSize));

    const ccode = params.ccode?.trim();
    if (ccode) {
      httpParams = httpParams.set('ccode', ccode);
    }

    const search = params.search?.trim();
    if (search) {
      httpParams = httpParams.set('search', search);
    }

    if (params.status && params.status !== 'All') {
      httpParams = httpParams.set('status', params.status);
    }

    if (params.clientProductId != null && params.clientProductId > 0) {
      httpParams = httpParams.set('clientProductId', String(params.clientProductId));
    }

    if (params.orderByField) {
      httpParams = httpParams.set('orderByField', params.orderByField);
    }
    if (params.orderBy) {
      httpParams = httpParams.set('orderBy', params.orderBy);
    }

    return this.http.get<PagedList<MemberListItemDto>>(`${this.apiUrl}/client/${mcode}/members`, {
      params: httpParams
    });
  }

  /**
   * GET /client/{mcode}/subclients/{ccode}/members/{subId}
   * (Angular UI route is `/group/.../subgroup/.../members/...`.)
   */
  getMemberDetail(mcode: string, ccode: string, subId: string | number): Observable<MemberDetailDto> {
    return this.http
      .get<unknown>(`${this.apiUrl}/client/${mcode}/subclients/${ccode}/members/${subId}`)
      .pipe(map((body) => normalizeMemberDetailResponse(body)));
  }

  /**
   * GET /client/{mcode}/subclients/{ccode}/members/{subId}/products — portal product rows.
   */
  getMemberProductsForPortal(
    mcode: string,
    ccode: string,
    subId: string | number,
    params?: GetMemberProductsForPortalParams
  ): Observable<MemberDetailProductDto[]> {
    let httpParams = new HttpParams();
    if (params?.activeOnly !== undefined) {
      httpParams = httpParams.set('activeOnly', String(params.activeOnly));
    }

    return this.http
      .get<unknown>(
        `${this.apiUrl}/client/${mcode}/subclients/${ccode}/members/${subId}/products`,
        { params: httpParams }
      )
      .pipe(map((body) => normalizeMemberProductsPortalResponse(body)));
  }

  /**
   * GET /client/{mcode}/subclients/{ccode}/members/{subId}/invoices — grouped invoice history (portal).
   */
  getMemberInvoices(
    mcode: string,
    ccode: string,
    subId: string | number
  ): Observable<MemberInvoicePortalDto[]> {
    return this.http
      .get<unknown>(
        `${this.apiUrl}/client/${mcode}/subclients/${ccode}/members/${subId}/invoices`
      )
      .pipe(map((body) => normalizeMemberInvoicesResponse(body)));
  }

  /**
   * PATCH /client/{mcode}/subclients/{ccode}/members/{subId} — partial update; 204 No Content.
   */
  patchMemberBasicInfo(
    mcode: string,
    ccode: string,
    subId: string | number,
    body: UpdateMemberBasicInfoDto
  ): Observable<void> {
    return this.http
      .patch<void>(`${this.apiUrl}/client/${mcode}/subclients/${ccode}/members/${subId}`, body)
      .pipe(map(() => undefined));
  }

  /**
   * GET /client/{mcode}/subclients/{ccode}/products/available — sub-client catalog (no subId).
   * Optional `effectiveDate` (`yyyy-MM-dd`, first of month) filters the catalog as of that day.
   * Omit to keep the API default (that company's payroll/OE base effective date).
   */
  getAvailableProducts(
    mcode: string,
    ccode: string,
    effectiveDate?: string
  ): Observable<SubClientAvailableProductDto[]> {
    const options = effectiveDate
      ? { params: new HttpParams().set('effectiveDate', effectiveDate) }
      : {};
    return this.http.get<SubClientAvailableProductDto[]>(
      `${this.apiUrl}/client/${mcode}/subclients/${ccode}/products/available`,
      options
    );
  }

  /**
   * POST /client/{mcode}/subclients/{ccode}/members/{subId}/products — 204 No Content.
   * Body includes clientProductId and optional effectiveDate (yyyy-MM-dd, first of month).
   */
  addMemberProduct(
    mcode: string,
    ccode: string,
    subId: string | number,
    body: AddMemberProductRequestDto
  ): Observable<void> {
    return this.http
      .post<void>(`${this.apiUrl}/client/${mcode}/subclients/${ccode}/members/${subId}/products`, body)
      .pipe(map(() => undefined));
  }

  /**
   * PATCH .../members/{subId}/products/{memberProductId}/cancel — 204 No Content.
   */
  cancelMemberProduct(
    mcode: string,
    ccode: string,
    subId: string | number,
    memberProductId: number,
    body: CancelMemberProductRequestDto
  ): Observable<void> {
    return this.http
      .patch<void>(
        `${this.apiUrl}/client/${mcode}/subclients/${ccode}/members/${subId}/products/${memberProductId}/cancel`,
        body
      )
      .pipe(map(() => undefined));
  }

  /**
   * PATCH /client/{mcode}/members/products/bulk-cancel — 204 No Content.
   * Cancels all active member products for each (subId, cCode) in `targets`.
   */
  bulkCancelMemberProducts(mcode: string, body: BulkCancelMemberProductsRequestDto): Observable<void> {
    return this.http
      .patch<void>(`${this.apiUrl}/client/${mcode}/members/products/bulk-cancel`, body)
      .pipe(map(() => undefined));
  }

  /**
   * POST /client/.../members/{subId}/dependents — 204 No Content.
   */
  addMemberDependent(
    mcode: string,
    ccode: string,
    subId: string | number,
    body: MemberDependentRequestDto
  ): Observable<void> {
    return this.http
      .post<void>(`${this.apiUrl}/client/${mcode}/subclients/${ccode}/members/${subId}/dependents`, body)
      .pipe(map(() => undefined));
  }

  /**
   * PUT /client/.../members/{subId}/dependents/{dependentId} — 204 No Content.
   */
  updateMemberDependent(
    mcode: string,
    ccode: string,
    subId: string | number,
    dependentId: number,
    body: MemberDependentRequestDto
  ): Observable<void> {
    return this.http
      .put<void>(
        `${this.apiUrl}/client/${mcode}/subclients/${ccode}/members/${subId}/dependents/${dependentId}`,
        body
      )
      .pipe(map(() => undefined));
  }

  /**
   * DELETE /client/.../members/{subId}/dependents/{dependentId} — JSON body required; 204 No Content.
   */
  removeMemberDependent(
    mcode: string,
    ccode: string,
    subId: string | number,
    dependentId: number,
    body: RemoveMemberDependentRequestDto
  ): Observable<void> {
    return this.http
      .delete<void>(
        `${this.apiUrl}/client/${mcode}/subclients/${ccode}/members/${subId}/dependents/${dependentId}`,
        { body }
      )
      .pipe(map(() => undefined));
  }
}
